import React, { useRef } from 'react';
import { usePrintHook } from '../../hooks/usePrintHook';
import { februaryData } from '../../data/februaryData';
import MarketingReport from '../MarketingReport';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const StandaloneFebruaryReport: React.FC = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { printRef } = usePrintHook();
  
  // Generate PDF from report
  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    try {
      // Create filename for the PDF
      const fileName = `ROI_Marketing_Report_February_2025.pdf`;
      
      // Configure dimensions
      const reportElement = reportRef.current;
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Higher scale for better quality
        useCORS: true, // Allow images from other domains
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Page setup (portrait A4)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate ratio to fit content to PDF
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;
      
      // Add content spread across multiple pages if needed
      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;
      
      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;
      
      // Add more pages if content overflows
      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }
      
      // Add footer on each page
      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }
      
      // Download PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  
  // Handle export to PDF
  const handleExportPDF = () => {
    generatePDF();
  };
  
  // Handle print
  const handlePrint = () => {
    if (reportRef.current) {
      // Cast the ref to the expected type
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              February 2025 Marketing Report
            </h2>
            <div className="flex space-x-3">
              <button
                onClick={handlePrint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
              >
                Print
              </button>
              <button
                onClick={handleExportPDF}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Export PDF
              </button>
            </div>
          </div>

        </div>
        
        <div className="border p-4 rounded shadow-sm mb-4 bg-blue-50">
          <h3 className="text-lg font-semibold mb-2">February 2025 Report</h3>
          <p className="mb-3">
            This is a standalone February 2025 report using data from the February 2025 CSV files.
          </p>
        </div>
        
        {/* Wrap report in a div with ref for PDF export */}
        <div ref={reportRef}>
          <MarketingReport data={februaryData} />
        </div>
      </div>
    </div>
  );
};

export default StandaloneFebruaryReport;